import { useState, useEffect } from "react";
import { Camera, Plus, Trash2, Copy, Check, User, FolderOpen, Clock, ExternalLink } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PhotographersTab = () => {
  const [photographers, setPhotographers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [photosRes, invitesRes] = await Promise.all([
        axios.get(`${API}/photographers/admin/list`),
        axios.get(`${API}/photographers/admin/invites`)
      ]);
      setPhotographers(photosRes.data || []);
      setInvites(invitesRes.data || []);
    } catch (err) {
      console.error("Error fetching photographers:", err);
    } finally {
      setLoading(false);
    }
  };

  const generateInvite = async () => {
    setGeneratingInvite(true);
    try {
      const res = await axios.post(`${API}/photographers/admin/invite`);
      setInvites([res.data, ...invites]);
      copyToClipboard(res.data.code, `${window.location.origin}/photographer/signup?code=${res.data.code}`);
    } catch (err) {
      alert("Error generating invite");
    } finally {
      setGeneratingInvite(false);
    }
  };

  const deleteInvite = async (code) => {
    try {
      await axios.delete(`${API}/photographers/admin/invite/${code}`);
      setInvites(invites.filter(i => i.code !== code));
    } catch (err) {
      alert("Error deleting invite");
    }
  };

  const deletePhotographer = async (id) => {
    if (!window.confirm("Delete this photographer and all their galleries?")) return;
    try {
      await axios.delete(`${API}/photographers/admin/${id}`);
      setPhotographers(photographers.filter(p => p.id !== id));
    } catch (err) {
      alert("Error deleting photographer");
    }
  };

  const copyToClipboard = (code, fullUrl = null) => {
    const textToCopy = fullUrl || `${window.location.origin}/photographer/signup?code=${code}`;
    navigator.clipboard.writeText(textToCopy);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="photographers-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Photographers</h2>
          <p className="text-zinc-500 text-sm mt-1">Invite photographers to upload photos to galleries</p>
        </div>
        <button
          onClick={generateInvite}
          disabled={generatingInvite}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {generatingInvite ? "Generating..." : "Generate Invite"}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-zinc-900/50 border border-white/5 p-4">
          <div className="text-2xl font-bold text-white">{photographers.length}</div>
          <div className="text-zinc-500 text-sm">Total Photographers</div>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 p-4">
          <div className="text-2xl font-bold text-white">{invites.length}</div>
          <div className="text-zinc-500 text-sm">Pending Invites</div>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 p-4">
          <div className="text-2xl font-bold text-white">
            {photographers.reduce((sum, p) => sum + (p.gallery_count || 0), 0)}
          </div>
          <div className="text-zinc-500 text-sm">Total Galleries</div>
        </div>
      </div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <div>
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-500" />
            Pending Invites
          </h3>
          <div className="space-y-2">
            {invites.map((invite) => (
              <div
                key={invite.code}
                className="bg-zinc-900/50 border border-white/5 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <code className="bg-black px-3 py-1 text-orange-500 font-mono text-sm">
                    {invite.code}
                  </code>
                  <span className="text-zinc-500 text-sm">
                    Expires: {new Date(invite.expires_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(invite.code)}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-zinc-800 text-white hover:bg-zinc-700"
                  >
                    {copiedCode === invite.code ? (
                      <>
                        <Check className="w-3 h-3 text-green-500" /> Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" /> Copy Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => deleteInvite(invite.code)}
                    className="p-2 text-zinc-500 hover:text-red-500"
                    title="Delete invite"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photographers List */}
      <div>
        <h3 className="text-white font-bold mb-4 flex items-center gap-2">
          <Camera className="w-4 h-4 text-orange-500" />
          Registered Photographers
        </h3>

        {photographers.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/30 border border-white/5">
            <User className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">No Photographers Yet</h3>
            <p className="text-zinc-500 text-sm">Generate an invite link and share it with photographers</p>
          </div>
        ) : (
          <div className="space-y-2">
            {photographers.map((photographer) => (
              <div
                key={photographer.id}
                className="bg-zinc-900/50 border border-white/5 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden">
                    {photographer.profile_photo_url ? (
                      <img
                        src={photographer.profile_photo_url}
                        alt={photographer.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-6 h-6 text-zinc-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-white font-bold">{photographer.name}</h4>
                    <p className="text-zinc-500 text-sm">@{photographer.username}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 text-zinc-500 text-sm">
                    <FolderOpen className="w-4 h-4" />
                    {photographer.gallery_count || 0} galleries
                  </div>
                  
                  {photographer.website && (
                    <a
                      href={photographer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-500 hover:text-white"
                      title="Website"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}

                  <button
                    onClick={() => deletePhotographer(photographer.id)}
                    className="p-2 text-zinc-500 hover:text-red-500"
                    title="Delete photographer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotographersTab;
